import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Card,
  CardBody,
  Col,
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  UncontrolledDropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Form,
  Label,
  Input,
  FormFeedback,
  Alert,
  Spinner,
} from "reactstrap";
import { useTranslation } from "react-i18next";
import { DebounceInput } from "react-debounce-input";
import DataTable from "react-data-table-component";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";

import {
  addCity,
  getCities,
  getCity,
  updateCity,
  deleteCity,
  toggleCityModalAction,
} from "../../store/locations/cities/actions";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import { getGovernoratesSelectList } from "../../store/locations/governorates/actions";

const CitiesPage = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();

  const {
    cities,
    loadingCities,
    city,
    addCityError,
    addCityLoading,
    addCitySuccess,
    updateCityLoading,
    updateCitySuccess,
  } = useSelector((state) => state.Cities);

  const { governoratesSelectList } = useSelector((state) => state.Governorates);

  document.title = `${t("Cities.Cities")} | ${t(
    process.env.REACT_APP_HEADING
  )}`;

  const [isCityModalOpen, setIsCityModalOpen] = useState(false);

  const toggleCityModal = () => {
    if (!isCityModalOpen) {
      dispatch(getGovernoratesSelectList());
      dispatch(toggleCityModalAction());
      cityForm.resetForm({
        name: "",
        arabicName: "",
        governorateId: "",
      });
    }
    setIsCityModalOpen(!isCityModalOpen);
  };
  const [isCityDeleteModalOpen, setIsCityDeleteModalOpen] = useState(false);

  const toggleCityDeleteModal = () => {
    setIsCityDeleteModalOpen(!isCityDeleteModalOpen);
  };

  const [request, setRequest] = useState({
    limit: 10,
    page: 1,
    sortBy: "createdAt",
    sortDirection: "DESC",
  });

  const [totalRows, setTotalRows] = useState(0);

  const columns = [
    {
      name: <span className="font-weight-bold fs-13">{t("Fields.Name")}</span>,
      selector: (row) => (
        <div className="d-flex gap-2 align-items-center">
          <div className="flex-grow-1">
            {i18n.language === "ar" ? row.arabicName : row.name}
          </div>
        </div>
      ),
      sortable: true,
    },
    {
      name: (
        <span className="font-weight-bold fs-13">
          {t("Fields.Governorate")}
        </span>
      ),
      selector: (row) => (
        <div className="d-flex gap-2 align-items-center">
          <div className="flex-grow-1">
            {(i18n.language === "ar"
              ? row.governorate?.arabicName
              : row.governorate?.name) || "-"}
          </div>
        </div>
      ),
      sortable: false,
    },
    {
      name: (
        <span className="font-weight-bold fs-13">{t("Common.Created At")}</span>
      ),
      selector: (row) => new Date(row.createdAt).toLocaleDateString(),
      sortable: true,
    },
    {
      name: (
        <span className="font-weight-bold fs-13">{t("Common.Action")}</span>
      ),
      sortable: false,
      cell: (row) => {
        return (
          <React.Fragment>
            <Button
              color="primary"
              className="btn-icon btn-sm"
              onClick={() => onEdit(row.id)}
            >
              <i className="ri-pencil-fill" />
            </Button>
            &nbsp;
            <Button
              color="danger"
              className="btn-icon btn-sm"
              onClick={() => onDelete(row.id)}
            >
              <i className="ri-delete-bin-5-line" />
            </Button>
          </React.Fragment>
        );
      },
    },
  ];

  const [rows, setRows] = useState([]);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    let query = {
      limit: queryParams.has("limit") ? queryParams.get("limit") : 10,
      page: queryParams.has("page") ? queryParams.get("page") : 1,
      sortBy: queryParams.has("sortBy")
        ? queryParams.get("sortBy").split(":")[0]
        : "createdAt",
      sortDirection: queryParams.has("sortBy")
        ? queryParams.get("sortBy").split(":")[1]
        : "DESC",
      search: queryParams.has("search") ? queryParams.get("search") : "",
    };
    setDefaultSortAsc(query.sortDirection === "ASC");
    setDefaultSortFieldId(getSortByColumnId(query.sortBy));
    setRequest(query);
    dispatch(getCities(query));
  }, []);

  useEffect(() => {
    const { limit, page, search, sortBy, sortDirection } = request;
    let query;
    if (page) query = `?page=${page}`;
    if (limit) query += `&limit=${limit}`;
    if (search && search !== "") {
      history.replace({
        search: `&search=${search}`,
      });
    }
    if (sortBy && sortDirection) query += `&sortBy=${sortBy}:${sortDirection}`;
    if (search) query += `&search=${search}`;
    history.push({ search: query });
  }, [request]);

  useEffect(() => {
    setRows(cities?.data);
    setTotalRows(cities.meta?.totalItems);
  }, [cities, loadingCities]);

  const onChangeRowsPerPage = (pageSize) => {
    setRequest({ ...request, limit: pageSize });
    dispatch(getCities({ ...request, limit: pageSize }));
  };

  const onChangePage = (page) => {
    setRequest({ ...request, page: page });
    dispatch(getCities({ ...request, page: page }));
  };

  const onSearch = (text) => {
    setRequest({ ...request, search: text });
    dispatch(getCities({ ...request, search: text }));
  };

  useEffect(() => {
    if (addCitySuccess === true || updateCitySuccess === true) {
      toggleCityModal();
      dispatch(getCities(request));
    }
  }, [addCitySuccess, updateCitySuccess]);

  const getSortByColumnName = (id) => {
    let sortBy;
    if (i18n.language === "en") {
      switch (id) {
        case 1:
          sortBy = "name";
          break;
        default:
          sortBy = "createdAt";
          break;
      }
    }
    if (i18n.language === "ar") {
      switch (id) {
        case 1:
          sortBy = "arabicName";
          break;
        default:
          sortBy = "createdAt";
          break;
      }
    }
    return sortBy;
  };

  const getSortByColumnId = (name) => {
    let sortBy;
    switch (name) {
      case "name":
        sortBy = 1;
        break;
      case "arabicName":
        sortBy = 1;
        break;
      default:
        sortBy = 3;
        break;
    }
    return sortBy;
  };

  const onSort = (column, sortDirection) => {
    const sortBy = getSortByColumnName(column.id);
    setRequest({
      ...request,
      sortBy: sortBy,
      sortDirection: sortDirection.toUpperCase(),
    });
    dispatch(
      getCities({
        ...request,
        sortDirection: sortDirection.toUpperCase(),
        sortBy: sortBy,
      })
    );
  };

  const [defaultSortFieldId, setDefaultSortFieldId] = useState(3);
  const [defaultSortAsc, setDefaultSortAsc] = useState(false);

  /**
   * Formik handling
   */
  const cityForm = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: city.name || "",
      arabicName: city.arabicName || "",
      governorateId: city.governorateId || "",
    },

    validationSchema: Yup.object({
      name: Yup.string()
        .max(50, t("Validations.Common.Max", { length: 50 }))
        .required(t("Validations.Name.Required")),
      arabicName: Yup.string()
        .max(50, t("Validations.Common.Max", { length: 50 }))
        .required(t("Validations.ArabicName.Required")),
      governorateId: Yup.string().required(
        t("Validations.Governorate.Required")
      ),
    }),

    onSubmit: (values) => {
      if (selectedCityId) {
        // update
        dispatch(
          updateCity({
            ...values,
            id: selectedCityId,
          })
        );
      } else {
        dispatch(addCity({ ...values }));
      }
    },
  });

  const [selectedCityId, setSelectedCityId] = useState(null);

  const onEdit = (id) => {
    setSelectedCityId(id);
    dispatch(getCity(id));
    toggleCityModal();
  };

  const onDelete = (id) => {
    setSelectedCityId(id);
    toggleCityDeleteModal();
  };

  const onDeleteConfirmation = () => {
    dispatch(deleteCity(selectedCityId));
    toggleCityDeleteModal();
    setTimeout(() => {
      dispatch(getCities());
      setSelectedCityId(null);
    }, 3000);
  };

  const onAdd = () => {
    setSelectedCityId(null);
    toggleCityModal();
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            title={t("Cities.Cities")}
            pageTitle={t("Common.Users")}
          />
          <Row>
            <Col lg={12}>
              <Card>
                <CardBody>
                  <Row className="g-4 mb-3">
                    <Col className="col-sm-auto">
                      <div className="">
                        <div className="search-box ms-2">
                          <DebounceInput
                            className="form-control search"
                            placeholder={`${t("Common.Search")}...`}
                            minLength={2}
                            debounceTimeout={300}
                            onChange={(event) => onSearch(event.target.value)}
                            value={request.search}
                          />
                          <i className="ri-search-line search-icon"></i>
                        </div>
                      </div>
                    </Col>
                    <Col className="col-sm">
                      <div className="d-flex justify-content-sm-end">
                        <Button
                          color="success"
                          className="add-btn me-1"
                          id="create-btn"
                          onClick={onAdd}
                        >
                          <i className="ri-add-line align-bottom me-1"></i>{" "}
                          {t("Common.Add")}
                        </Button>
                      </div>
                    </Col>
                  </Row>
                  <DataTable
                    columns={columns}
                    data={rows}
                    progressPending={loadingCities}
                    progressComponent={
                      <Spinner
                        size="sm"
                        className="flex-shrink-0 m-5"
                        role="status"
                      ></Spinner>
                    }
                    pagination
                    paginationServer
                    sortServer
                    onSort={onSort}
                    onChangeRowsPerPage={onChangeRowsPerPage}
                    onChangePage={onChangePage}
                    defaultSortFieldId={defaultSortFieldId}
                    defaultSortAsc={defaultSortAsc}
                    persistTableHead
                    paginationTotalRows={totalRows}
                    paginationComponentOptions={{
                      rowsPerPageText: t("Pagination.Rows per page"),
                    }}
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
      {/* Add Modal */}
      <Modal
        isOpen={isCityModalOpen}
        toggle={() => {
          toggleCityModal();
        }}
        centered
      >
        <ModalHeader className="bg-light p-3">
          {selectedCityId ? t("Cities.Edit City") : t("Cities.Add City")}
          <Button
            type="button"
            onClick={() => {
              setIsCityModalOpen(false);
            }}
            className="btn-close"
            aria-label={t("Common.Close")}
          ></Button>
        </ModalHeader>
        <Form
          onSubmit={(e) => {
            e.preventDefault();
            cityForm.handleSubmit();
            return false;
          }}
          action="#"
        >
          <ModalBody>
            {addCityError && addCityError ? (
              <Alert color="danger">
                {" "}
                {t(`Messages.Error.${addCityError}`)}{" "}
              </Alert>
            ) : null}
            <div className="mb-3">
              <Label htmlFor="name" className="form-label">
                {t("Fields.Governorate")}*
              </Label>
              <select
                id="governorateId"
                className="form-select mb-3"
                aria-label={t("Fields.Select Governorate")}
                value={cityForm.values.governorateId}
                onChange={cityForm.handleChange}
                onBlur={cityForm.handleBlur}
                invalid={
                  cityForm.touched.governorateId &&
                  cityForm.errors.governorateId
                    ? true
                    : false
                }
              >
                <option>{t("Fields.Select Governorate")} </option>
                {governoratesSelectList &&
                  governoratesSelectList.map(({ id, value }) => (
                    <option value={id} key={id}>
                      {i18n.language === "ar" ? value.ar : value.en}
                    </option>
                  ))}
              </select>
              {cityForm.touched.governorateId &&
              cityForm.errors.governorateId ? (
                <FormFeedback type="invalid">
                  {cityForm.errors.governorateId}
                </FormFeedback>
              ) : null}
            </div>
            <div className="mb-3">
              <Label htmlFor="name" className="form-label">
                {t("Fields.Name")}*
              </Label>
              <Input
                type="text"
                className="form-control"
                id="name"
                placeholder={t("Fields.Enter Name")}
                value={cityForm.values.name || ""}
                onChange={cityForm.handleChange}
                onBlur={cityForm.handleBlur}
                invalid={
                  cityForm.touched.name && cityForm.errors.name ? true : false
                }
              />
              {cityForm.touched.name && cityForm.errors.name ? (
                <FormFeedback type="invalid">
                  {cityForm.errors.name}
                </FormFeedback>
              ) : null}
            </div>

            <div className="mb-3">
              <Label htmlFor="arabicName" className="form-label">
                {t("Fields.Arabic Name")}*
              </Label>
              <Input
                type="text"
                className="form-control"
                id="arabicName"
                placeholder={t("Fields.Enter Arabic Name")}
                value={cityForm.values.arabicName || ""}
                onChange={cityForm.handleChange}
                onBlur={cityForm.handleBlur}
                invalid={
                  cityForm.touched.arabicName && cityForm.errors.arabicName
                    ? true
                    : false
                }
              />
              {cityForm.touched.arabicName && cityForm.errors.arabicName ? (
                <FormFeedback type="invalid">
                  {cityForm.errors.arabicName}
                </FormFeedback>
              ) : null}
            </div>
          </ModalBody>
          <ModalFooter>
            <div className="hstack gap-2 justify-content-end">
              <button
                type="button"
                className="btn btn-light"
                onClick={() => setIsCityModalOpen(false)}
              >
                {t("Common.Close")}
              </button>
              <button
                type="submit"
                className="btn btn-success"
                id="add-btn"
                disabled={addCityLoading || updateCityLoading}
              >
                {addCityLoading || updateCityLoading ? (
                  <span className="d-flex align-items-center">
                    <span className="flex-grow-1 me-2">
                      {t("Common.Saving")}...
                    </span>
                    <Spinner size="sm" className="flex-shrink-0" role="status">
                      {" "}
                      {t("Common.Saving")}
                      ...{" "}
                    </Spinner>
                  </span>
                ) : (
                  <>{t("Common.Save")}</>
                )}
              </button>
            </div>
          </ModalFooter>
        </Form>
      </Modal>

      {/* Remove Modal */}
      <Modal
        isOpen={isCityDeleteModalOpen}
        toggle={() => {
          toggleCityDeleteModal();
        }}
        centered
      >
        <div className="modal-header">
          <Button
            type="button"
            onClick={() => setIsCityDeleteModalOpen(false)}
            className="btn-close"
            aria-label="Close"
          >
            {" "}
          </Button>
        </div>
        <ModalBody>
          <div className="mt-2 text-center">
            <lord-icon
              src="https://cdn.lordicon.com/gsqxdxog.json"
              trigger="loop"
              colors="primary:#f7b84b,secondary:#f06548"
              style={{ width: "100px", height: "100px" }}
            ></lord-icon>
            <div className="mt-4 pt-2 fs-15 mx-4 mx-sm-5">
              <h4>{t("Common.Are you Sure?")}</h4>
              <p className="text-muted mx-4 mb-0">
                {t("Common.Are you Sure You want to Remove this Record?")}
              </p>
            </div>
          </div>
          <div className="d-flex gap-2 justify-content-center mt-4 mb-2">
            <button
              type="button"
              className="btn w-sm btn-light"
              onClick={() => {
                setSelectedCityId(null);
                toggleCityDeleteModal();
              }}
            >
              {t("Common.Close")}
            </button>
            <button
              type="button"
              className="btn w-sm btn-danger "
              id="delete-record"
              onClick={() => onDeleteConfirmation()}
            >
              {t("Common.Yes, Delete It!")}
            </button>
          </div>
        </ModalBody>
      </Modal>
    </React.Fragment>
  );
};

export default CitiesPage;
